import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { HelpHint } from "../components/HelpTooltip";
import { CardValuesOverlay } from "../components/CardValuesOverlay";
import { CardScanOverlay } from "../components/CardScanOverlay";
import { SparklesIcon } from "../components/SparklesIcon";
import { useAuth } from "../context/AuthContext";
import { useBreadcrumbs } from "../context/BreadcrumbContext";
import { api, fetchCardScanEnabled } from "../lib/api";
import { viewerScanPoints } from "../lib/card-scan-sides";
import type {
  CoincheState,
  ContractFamily,
  QuickRuleset,
} from "../types/domain";

type TrumpKey =
  | "spades"
  | "hearts"
  | "diamonds"
  | "clubs"
  | "sans_atout"
  | "tout_atout";

const TRUMP_OPTIONS: { label: string; value: TrumpKey }[] = [
  { label: "♠", value: "spades" },
  { label: "♥", value: "hearts" },
  { label: "♦", value: "diamonds" },
  { label: "♣", value: "clubs" },
  { label: "TA", value: "tout_atout" },
  { label: "SA", value: "sans_atout" },
];

const COINCHE_OPTIONS: { label: string; value: CoincheState }[] = [
  { label: "Coinche", value: "coinche" },
  { label: "Surcoinche", value: "surcoinche" },
];

export function allowedTrumps(
  ruleset: Pick<QuickRuleset, "sansAtoutEnabled" | "toutAtoutEnabled">,
): TrumpKey[] {
  return TRUMP_OPTIONS.map((o) => o.value).filter((t) => {
    if (t === "sans_atout") return ruleset.sansAtoutEnabled;
    if (t === "tout_atout") return ruleset.toutAtoutEnabled;
    return true;
  });
}

export function allowedCoincheStates(
  ruleset: Pick<QuickRuleset, "surcoincheEnabled">,
): CoincheState[] {
  return COINCHE_OPTIONS.map((o) => o.value).filter((s) => {
    if (s === "surcoinche") return ruleset.surcoincheEnabled;
    return true;
  });
}

const CONTRACT_MIN = 80;
const CONTRACT_MAX = 180;
const CONTRACT_STEP = 10;

const POINTS_MIN = 0;
const POINTS_MAX = 162;

export function clampContract(value: number): number {
  return Math.max(CONTRACT_MIN, Math.min(CONTRACT_MAX, value));
}

export function pointsForGenerale(
  takerSide: "left" | "right",
): [number, number] {
  return takerSide === "left"
    ? [POINTS_MAX, POINTS_MIN]
    : [POINTS_MIN, POINTS_MAX];
}

const PREVIEW_DEBOUNCE_MS = 400;

export function NewHandPage() {
  const { gameId = "", handId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { setTrail } = useBreadcrumbs();
  const isEdit = Boolean(handId);

  const [swapped, setSwapped] = useState(false);
  const [takerSide, setTakerSide] = useState<"left" | "right">("left");
  const [contractFamily, setContractFamily] =
    useState<ContractFamily>("couleur");
  const [contractValue, setContractValue] = useState(110);
  const [trump, setTrump] = useState<TrumpKey>("diamonds");
  const [pointsLeft, setPointsLeft] = useState(82);
  const [pointsRight, setPointsRight] = useState(80);
  const [beloteLeft, setBeloteLeft] = useState(0);
  const [beloteRight, setBeloteRight] = useState(0);
  const [generaleMade, setGeneraleMade] = useState(false);
  const [coincheState, setCoincheState] = useState<CoincheState>("simple");
  const [foulSide, setFoulSide] = useState<"left" | "right" | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [readOnly, setReadOnly] = useState(false);
  const [showCardValues, setShowCardValues] = useState(false);
  const [showScan, setShowScan] = useState(false);
  const [scanEnabled, setScanEnabled] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void fetchCardScanEnabled().then((enabled) => {
      if (!cancelled) setScanEnabled(enabled);
    });
    return () => {
      cancelled = true;
    };
  }, []);
  const scanRevision = useRef(0);
  const [scanUndo, setScanUndo] = useState<{
    left: number;
    right: number;
    generale: boolean;
  } | null>(null);
  const [scanNotice, setScanNotice] = useState<string | null>(null);
  const [ruleset, setRuleset] = useState<QuickRuleset>({
    beloteEnabled: true,
    sansAtoutEnabled: true,
    toutAtoutEnabled: true,
    surcoincheEnabled: true,
    foulEnabled: true,
  });

  const flip = (side: "left" | "right") => (side === "left" ? "right" : "left");

  useEffect(() => {
    let active = true;
    setTrail([]);
    void api.getGame(gameId).then((game) => {
      if (!active) return;
      const isSwapped = game.mySide === "right";
      setSwapped(isSwapped);
      setRuleset(game.ruleset);
      setTrump((prev) => {
        const allowed = allowedTrumps(game.ruleset);
        return allowed.includes(prev) ? prev : (allowed[0] ?? "diamonds");
      });

      const hand = handId ? game.hands.find((h) => h.id === handId) : undefined;
      const pageLabel = hand ? `Levée #${hand.orderIndex}` : "Nouvelle levée";
      setTrail([
        { label: "Parties", to: "/games" },
        { label: game.title, to: `/games/${gameId}` },
        { label: pageLabel },
      ]);

      const canWrite = game.isParticipant || (user?.isAdmin ?? false);
      setReadOnly(!game.handsEditable || !canWrite);
      if (!handId) {
        setLoaded(true);
        return;
      }
      if (hand) {
        setTakerSide(hand.takerSide);
        setContractFamily(hand.contractFamily);
        setContractValue(hand.contractValue ?? 110);
        setTrump(hand.trump as TrumpKey);
        setPointsLeft(hand.pointsLeft);
        setPointsRight(hand.pointsRight);
        setBeloteLeft(hand.beloteLeft);
        setBeloteRight(hand.beloteRight);
        setGeneraleMade(hand.generaleMade);
        setCoincheState(hand.coincheState);
        setFoulSide(hand.foulSide);
      }
      setLoaded(true);
    });
    return () => {
      active = false;
    };
  }, [gameId, handId, isEdit, setTrail]);
  const [preview, setPreview] = useState<{
    leftScore: number;
    rightScore: number;
    success: boolean;
  } | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  const isSpecial = contractFamily === "capot" || contractFamily === "generale";

  useEffect(() => {
    scanRevision.current++;
    setShowScan(false);
    setScanUndo(null);
    setScanNotice(null);
  }, [
    gameId,
    handId,
    trump,
    foulSide,
    readOnly,
    swapped,
    takerSide,
    contractFamily,
  ]);

  useEffect(() => {
    clearTimeout(debounceRef.current);
    let active = true;
    setPreview(null);
    debounceRef.current = setTimeout(() => {
      void api
        .previewScore({
          takerSide: swapped ? flip(takerSide) : takerSide,
          contractFamily,
          contractValue: isSpecial ? null : contractValue,
          trump,
          pointsLeft: swapped ? pointsRight : pointsLeft,
          pointsRight: swapped ? pointsLeft : pointsRight,
          beloteLeft: swapped ? beloteRight : beloteLeft,
          beloteRight: swapped ? beloteLeft : beloteRight,
          generaleMade,
          coincheState,
          foulSide: swapped && foulSide ? flip(foulSide) : foulSide,
        })
        .then((result) => {
          if (!active) return;
          if (!result) return setPreview(null);
          setPreview(
            swapped
              ? {
                  leftScore: result.rightScore,
                  rightScore: result.leftScore,
                  success: result.success,
                }
              : result,
          );
        });
    }, PREVIEW_DEBOUNCE_MS);
    return () => {
      active = false;
      clearTimeout(debounceRef.current);
    };
  }, [
    takerSide,
    contractFamily,
    contractValue,
    trump,
    pointsLeft,
    pointsRight,
    beloteLeft,
    beloteRight,
    generaleMade,
    coincheState,
    foulSide,
    isSpecial,
    swapped,
  ]);

  function handlePointsLeftChange(value: number) {
    scanRevision.current++;
    setScanUndo(null);
    setScanNotice(null);
    const clamped = Math.max(POINTS_MIN, Math.min(POINTS_MAX, value));
    setPointsLeft(clamped);
    setPointsRight(POINTS_MAX - clamped);
    setGeneraleMade(false);
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    scanRevision.current++;
    setShowScan(false);
    setSubmitting(true);
    const payload = {
      takerSide: swapped ? flip(takerSide) : takerSide,
      contractFamily,
      contractValue: isSpecial ? null : contractValue,
      trump,
      pointsLeft: swapped ? pointsRight : pointsLeft,
      pointsRight: swapped ? pointsLeft : pointsRight,
      beloteLeft: swapped ? beloteRight : beloteLeft,
      beloteRight: swapped ? beloteLeft : beloteRight,
      generaleMade,
      coincheState,
      foulSide: swapped && foulSide ? flip(foulSide) : foulSide,
    };
    const action = isEdit
      ? api.updateHand(gameId, handId!, payload)
      : api.addHand(gameId, payload);
    void action
      .then(() => navigate(`/games/${gameId}`))
      .finally(() => setSubmitting(false));
  }

  if (!loaded) return null;

  return (
    <div className="mx-auto max-w-lg px-4 py-6">
      <form
        className={`space-y-6 ${readOnly ? "pointer-events-none opacity-70" : ""}`}
        onSubmit={handleSubmit}
      >
        {/* Preneur */}
        <section>
          <p className="label">Preneur</p>
          <div className="grid grid-cols-2 gap-3">
            {(["left", "right"] as const).map((side) => (
              <button
                key={side}
                type="button"
                className={
                  takerSide === side
                    ? "btn-secondary !border-ember !text-ember"
                    : "btn-secondary"
                }
                onClick={() => {
                  setTakerSide(side);
                  if (generaleMade) {
                    const [left, right] = pointsForGenerale(side);
                    setPointsLeft(left);
                    setPointsRight(right);
                  }
                }}
              >
                {side === "left" ? "Nous" : "Eux"}
              </button>
            ))}
          </div>
        </section>

        {/* Contrat */}
        <section>
          <p className="label">Contrat</p>
          <div className="card space-y-4">
            {/* Slider */}
            {!isSpecial && (
              <div>
                <div className="mb-2 text-center text-3xl font-bold text-ember tabular-nums">
                  {contractValue}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    aria-label="Diminuer le contrat"
                    disabled={contractValue <= CONTRACT_MIN}
                    className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50 active:scale-[0.98] transition disabled:cursor-not-allowed disabled:opacity-50"
                    onClick={() => {
                      const next = clampContract(contractValue - CONTRACT_STEP);
                      setContractValue(next);
                      setContractFamily("couleur");
                    }}
                  >
                    -
                  </button>
                  <input
                    type="range"
                    min={CONTRACT_MIN}
                    max={CONTRACT_MAX}
                    step={CONTRACT_STEP}
                    value={contractValue}
                    onChange={(e) => {
                      setContractValue(Number(e.target.value));
                      setContractFamily("couleur");
                    }}
                    className="flex-1"
                  />
                  <button
                    type="button"
                    aria-label="Augmenter le contrat"
                    disabled={contractValue >= CONTRACT_MAX}
                    className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50 active:scale-[0.98] transition disabled:cursor-not-allowed disabled:opacity-50"
                    onClick={() => {
                      const next = clampContract(contractValue + CONTRACT_STEP);
                      setContractValue(next);
                      setContractFamily("couleur");
                    }}
                  >
                    +
                  </button>
                </div>
                <div className="mt-1 flex justify-between text-xs text-smoke">
                  <span>{CONTRACT_MIN}</span>
                  <span>{CONTRACT_MAX}</span>
                </div>
              </div>
            )}

            {/* Capot / Générale */}
            <div className="grid grid-cols-2 gap-2">
              {(["capot", "generale"] as ContractFamily[]).map((fam) => (
                <button
                  key={fam}
                  type="button"
                  className={
                    contractFamily === fam
                      ? "btn-secondary !border-ember !text-ember"
                      : "btn-secondary"
                  }
                  onClick={() => {
                    const next = contractFamily === fam ? "couleur" : fam;
                    setContractFamily(next);
                    if (next !== "generale") setGeneraleMade(false);
                  }}
                >
                  {fam === "capot" ? "Capot" : "Générale"}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Atout / mode */}
        <section>
          <p className="label">Atout / mode</p>
          <div className="grid grid-cols-6 gap-2">
            {TRUMP_OPTIONS.filter(({ value }) => {
              if (value === "sans_atout") return ruleset.sansAtoutEnabled;
              if (value === "tout_atout") return ruleset.toutAtoutEnabled;
              return true;
            }).map(({ label, value }) => {
              const isRed = value === "hearts" || value === "diamonds";
              const isBlack = value === "spades" || value === "clubs";
              const activeClass = isRed
                ? "!border-red-500 !text-red-500"
                : isBlack
                  ? "!border-ink !text-ink"
                  : "!border-ember !text-ember";
              return (
                <button
                  key={value}
                  type="button"
                  className={`${
                    trump === value
                      ? `btn-secondary ${activeClass}`
                      : "btn-secondary"
                  } text-lg`}
                  onClick={() => {
                    setTrump(value);
                    if (value === "sans_atout") {
                      setBeloteLeft(0);
                      setBeloteRight(0);
                    } else if (value !== "tout_atout") {
                      setBeloteLeft((v) => Math.min(v, 1));
                      setBeloteRight((v) => Math.min(v, 1));
                    }
                  }}
                >
                  {label}
                </button>
              );
            })}
          </div>
        </section>

        {/* Points faits */}
        <section>
          <div className="mb-1.5 flex flex-wrap items-center justify-between gap-2">
            <p className="label !mb-0">Points faits</p>
            <div className="flex items-center gap-2">
              <button
                type="button"
                className="pointer-events-auto flex h-9 w-9 items-center justify-center rounded-full text-smoke transition hover:bg-gray-100 hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ember"
                aria-label="Voir la valeur des cartes"
                onClick={() => setShowCardValues(true)}
              >
                <svg
                  viewBox="0 0 24 24"
                  className="h-5 w-5"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={2}
                  strokeLinecap="round"
                >
                  <circle cx="12" cy="12" r="9" />
                  <path d="M12 11v5" />
                  <path d="M12 8h.01" />
                </svg>
              </button>
            </div>
          </div>
          <div className="card">
            <div className="relative mb-2 flex items-baseline justify-between">
              <span className="w-12 text-xl font-bold text-ember tabular-nums">
                {pointsLeft === 162 ? "Capot" : pointsLeft}
              </span>
              <span className="absolute inset-x-0 text-center text-xs text-smoke">
                Nous / Eux
              </span>
              <span className="w-12 text-right text-xl font-bold text-ember tabular-nums">
                {pointsRight === 162 ? "Capot" : pointsRight}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                aria-label="Diminuer les points"
                disabled={pointsLeft <= POINTS_MIN}
                className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50 active:scale-[0.98] transition disabled:cursor-not-allowed disabled:opacity-50"
                onClick={() => handlePointsLeftChange(pointsLeft - 1)}
              >
                -
              </button>
              <input
                type="range"
                min={POINTS_MIN}
                max={POINTS_MAX}
                step={1}
                value={pointsLeft}
                onChange={(e) => handlePointsLeftChange(Number(e.target.value))}
                className="flex-1"
              />
              <button
                type="button"
                aria-label="Augmenter les points"
                disabled={pointsLeft >= POINTS_MAX}
                className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50 active:scale-[0.98] transition disabled:cursor-not-allowed disabled:opacity-50"
                onClick={() => handlePointsLeftChange(pointsLeft + 1)}
              >
                +
              </button>
            </div>
            <div className="mt-1 flex justify-between text-xs text-smoke">
              <span>{POINTS_MIN}</span>
              <span>{POINTS_MAX}</span>
            </div>
            {scanNotice && (
              <p role="status" className="mt-2 text-sm text-smoke">
                {scanNotice}
              </p>
            )}
            {scanUndo && (
              <button
                type="button"
                className="mt-2 text-sm underline"
                onClick={() => {
                  scanRevision.current++;
                  setPointsLeft(scanUndo.left);
                  setPointsRight(scanUndo.right);
                  setGeneraleMade(scanUndo.generale);
                  setScanUndo(null);
                  setScanNotice(null);
                }}
              >
                Annuler la proposition
              </button>
            )}
            {contractFamily === "generale" && (
              <button
                type="button"
                aria-pressed={generaleMade}
                className={`btn-secondary mt-4 w-full ${
                  generaleMade ? "!border-ember !text-ember" : ""
                }`}
                onClick={() => {
                  scanRevision.current++;
                  setShowScan(false);
                  setScanUndo(null);
                  const next = !generaleMade;
                  setGeneraleMade(next);
                  if (next) {
                    const [left, right] = pointsForGenerale(takerSide);
                    setPointsLeft(left);
                    setPointsRight(right);
                  }
                }}
              >
                Générale
              </button>
            )}
          </div>
          {scanEnabled && (
            <>
              <button
                type="button"
                className="btn-ai mt-3 w-full"
                disabled={readOnly || Boolean(foulSide) || submitting}
                onClick={() => setShowScan(true)}
              >
                <SparklesIcon />
                Scanner les cartes
              </button>
              {foulSide && (
                <p className="mt-2 text-sm text-smoke">
                  Le scanner est indisponible pour une faute de jeu.
                </p>
              )}
            </>
          )}
        </section>

        {/* Belote */}
        {trump !== "sans_atout" &&
          (() => {
            const isToutAtout = trump === "tout_atout";
            const maxBelote = isToutAtout ? 4 : 1;
            const helpText = isToutAtout
              ? "Appuyer pour incrémenter (0-4), revient à 0 après 4."
              : "Appuyer pour activer/désactiver la belote.";
            return (
              <section>
                <p className="label">Belote</p>
                <div className="grid grid-cols-2 gap-3">
                  {(
                    [
                      {
                        label: "Nous",
                        value: beloteLeft,
                        setter: setBeloteLeft,
                        otherSetter: setBeloteRight,
                      },
                      {
                        label: "Eux",
                        value: beloteRight,
                        setter: setBeloteRight,
                        otherSetter: setBeloteLeft,
                      },
                    ] as const
                  ).map(({ label, value, setter, otherSetter }) => (
                    <button
                      key={label}
                      type="button"
                      className="btn-secondary relative"
                      onClick={() => {
                        if (isToutAtout) {
                          setter((v) => (v >= maxBelote ? 0 : v + 1));
                        } else {
                          setter((v) => (v >= 1 ? 0 : 1));
                          if (value === 0) otherSetter(0);
                        }
                      }}
                    >
                      {label}
                      {value > 0 && (
                        <span className="absolute -top-1.5 -right-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-ember text-xs font-bold text-white">
                          {value}
                        </span>
                      )}
                    </button>
                  ))}
                </div>
                <HelpHint text={helpText} />
              </section>
            );
          })()}

        {/* Coinche */}
        <section>
          <p className="label">Coinche</p>
          <div className="grid grid-cols-2 gap-2">
            {COINCHE_OPTIONS.filter(({ value }) => {
              if (value === "surcoinche") return ruleset.surcoincheEnabled;
              return true;
            }).map(({ label, value }) => (
              <button
                key={value}
                type="button"
                className={
                  coincheState === value
                    ? "btn-secondary !border-ember !text-ember"
                    : "btn-secondary"
                }
                onClick={() =>
                  setCoincheState(coincheState === value ? "simple" : value)
                }
              >
                {label}
              </button>
            ))}
          </div>
        </section>

        {/* Faute */}
        <section>
          <p className="label">Faute de jeu</p>
          <div className="rounded-xl border border-red-200 bg-red-50 p-4">
            <div className="grid grid-cols-2 gap-3">
              {(["left", "right"] as const).map((side) => (
                <button
                  key={side}
                  type="button"
                  className={
                    foulSide === side
                      ? "btn-secondary !border-red-500 !text-red-500"
                      : "btn-secondary"
                  }
                  onClick={() => setFoulSide(foulSide === side ? null : side)}
                >
                  {side === "left" ? "Nous" : "Eux"}
                </button>
              ))}
            </div>
            <HelpHint text="À déclarer quand une équipe n'a pas fourni à l'atout, n'a pas coupé ou n'est pas montée alors qu'elle le pouvait. L'équipe fautive perd la levée et l'adversaire marque 160 points." />
          </div>
        </section>

        {/* Submit */}
        {!readOnly && (
          <button
            type="submit"
            className={`inline-flex w-full items-center justify-center rounded-xl px-5 py-3 text-base font-semibold text-white transition active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50 ${
              preview
                ? preview.leftScore >= preview.rightScore
                  ? "bg-emerald-600 hover:bg-emerald-700"
                  : "bg-amber-500 hover:bg-amber-600"
                : "bg-ember hover:bg-ember/90"
            }`}
            disabled={submitting}
          >
            {submitting ? (
              "Ajout en cours..."
            ) : preview ? (
              `Nous ${preview.leftScore} / Eux ${preview.rightScore}`
            ) : (
              <span className="inline-block h-5 w-5 animate-spin rounded-full border-2 border-white border-t-transparent" />
            )}
          </button>
        )}
      </form>
      {readOnly && (
        <Link
          to={`/games/${gameId}`}
          className="btn-secondary mt-4 inline-block"
        >
          Retour
        </Link>
      )}
      <CardValuesOverlay
        open={showCardValues}
        onClose={() => setShowCardValues(false)}
      />
      {scanEnabled && showScan && !readOnly && !foulSide && (
        <CardScanOverlay
          gameId={gameId}
          handId={handId}
          trump={trump}
          swapped={swapped}
          revision={scanRevision}
          onClose={() => setShowScan(false)}
          onApply={(left, right, version) => {
            if (
              version !== scanRevision.current ||
              readOnly ||
              foulSide ||
              submitting
            )
              return false;
            const [nextLeft, nextRight] = viewerScanPoints(
              left,
              right,
              swapped,
            );
            setScanUndo({
              left: pointsLeft,
              right: pointsRight,
              generale: generaleMade,
            });
            setPointsLeft(nextLeft);
            setPointsRight(nextRight);
            setGeneraleMade(false);
            setScanNotice(
              `Points proposés : Nous ${nextLeft} / Eux ${nextRight}. Vérifiez avant d'enregistrer.`,
            );
            scanRevision.current++;
            return true;
          }}
        />
      )}
    </div>
  );
}
